import React, { Suspense, lazy, useEffect, useState, useCallback } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { CssVarsProvider } from '@mui/joy/styles';
import { Box, CircularProgress } from '@mui/joy';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import messenger from './components/messenger';
import ProtectedRoute from './components/ProtectedRoute';

// Komponenten, die sofort benötigt werden
import Sidebar from './components/Sidebar';
import TopNav from './components/topnav';
import BreadcrumbsComponent from './components/BreadcrumbsComponent';
import GeneralSettings from './pages/GeneralSettings';

// Lazy-loaded Komponenten
const HomePage = lazy(() => import('./pages/homepage'));
const Page1 = lazy(() => import('./pages/Page1'));
const FleetPartner = lazy(() => import('./pages/FleetPartner'));
const AddPartner = lazy(() => import('./pages/AddPartner'));
const AddApplicant = lazy(() => import('./pages/AddApplicant'));
const PartnerDetails = lazy(() => import('./pages/PartnerDetails'));
const ApplicantDetails = lazy(() => import('./pages/ApplicantDetails'));
const Brands = lazy(() => import('./pages/brands'));
const Locations = lazy(() => import('./pages/locations'));
const Applicants = lazy(() => import('./pages/applicants'));
const LoginPage = lazy(() => import('./LoginPage'));
const Assets = lazy(() => import('./pages/assets'));
const UserManagement = lazy(() => import('./pages/user-management'));
const OrderList = lazy(() => import('./pages/OrderList'));
const BillingManagement = lazy(() => import('./pages/BillingManagement'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));

// Prefetching-Funktion
const prefetchComponent = (importFunc) => {
  importFunc();
};

// Optimierter LoadingFallback
const LoadingFallback = React.memo(() => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
));

// QueryClient mit optimierten Einstellungen
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000, // 5 Minuten
    },
  },
});

function App() {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, error } = useAuth0();
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSidebarResize = useCallback((width) => {
    setSidebarWidth(width);
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      if (!isLoading) {
        if (!isAuthenticated) {
          loginWithRedirect();
        } else {
          try {
            await getAccessTokenSilently();
            setIsTokenValid(true);
          } catch (e) {
            console.error("Token validation failed:", e);
            setIsTokenValid(false);
            loginWithRedirect();
          }
        }
      }
    };

    checkAuthStatus();
  }, [isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently]);

  // Prefetching-Logik
  useEffect(() => {
    if (isAuthenticated && isTokenValid) {
      if (location.pathname === '/') {
        prefetchComponent(() => import('./pages/Page1'));
        prefetchComponent(() => import('./pages/OrderList'));
      }
    }
  }, [isAuthenticated, isTokenValid, location]);

  if (isLoading || !isTokenValid) {
    return <LoadingFallback />;
  }

  if (error) {
    return <div>Fehler: {error.message}</div>;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CssVarsProvider>
        <messenger>
          <Box sx={{ display: 'flex', height: '100vh' }}>
            <Sidebar onResize={handleSidebarResize} />
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                flexGrow: 1,
                width: `calc(100% - ${sidebarWidth}px)`,
                marginLeft: `${sidebarWidth}px`,
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <TopNav />
              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}> {/* Removed padding: '20px' */}                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Öffentliche Routen */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />


                    {/* Geschützte Routen */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <HomePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/page1"
                      element={
                        <ProtectedRoute>
                          <Page1 />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/orderlist"
                      element={
                        <ProtectedRoute>
                          <OrderList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/FleetPartner"
                      element={
                        <ProtectedRoute>
                          <FleetPartner />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brands"
                      element={
                        <ProtectedRoute>
                          <Brands />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locations"
                      element={
                        <ProtectedRoute>
                          <Locations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/add-partner"
                      element={
                        <ProtectedRoute>
                          <AddPartner />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/partner-details/:partnerId"
                      element={
                        <ProtectedRoute>
                          <PartnerDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/assets"
                      element={
                        <ProtectedRoute>
                          <Assets />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/BillingManagement"
                      element={
                        <ProtectedRoute>
                          <BillingManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/applicants"
                      element={
                        <ProtectedRoute>
                          <Applicants />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/applicants/add"
                      element={
                        <ProtectedRoute>
                          <AddApplicant />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/applicant-details/:applicantId"
                      element={
                        <ProtectedRoute>
                          <ApplicantDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/user-management"
                      element={
                        <ProtectedRoute>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <GeneralSettings />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </Box>
            </Box>
          </Box>
          <Toaster />
        </messenger>
      </CssVarsProvider>
    </QueryClientProvider>
  );
}

export default App;